import { Input, Table, Tag } from "antd";
import useUsers from "hooks/useUsers";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { routesNames } from "utils/Constants";
import { getCurriculumPDF } from "services/Usuarios";
import useNotification from "hooks/useNotification";
import { FaFilePdf } from "react-icons/fa";
import { BiSearchAlt } from "react-icons/bi";
import { BASE_API } from "config/config";
import ExportExcel from "components/excel/ExportExcel";
import dayjs from "dayjs";

const DEFAULT_ESTADO_FILTER = ["Rechazado", "Activo", "Inactivo"];

const PostulantesTable = () => {
    const [value, setValue] = useState("");
    const [datasource, setDatasource] = useState([]);
    const { contextHolder, openNotification } = useNotification();
    // Ya no usamos 'response' directamente para la data principal
    const { isLoading, fetchData } = useUsers();
    const [dataExcel, setDataExcel] = useState([]);
    const [sort, setSort] = useState({
        orderBy: "last_login_at",
        orderDirection: "desc",
    });

    // 🆕 Filtros que se envían al backend (server-side)
    const [filters, setFilters] = useState({
        estado: DEFAULT_ESTADO_FILTER,
        verificado: [],
        disponible_ia: [],
        perfil_completo: [],
    });

    // 🆕 Estado para manejar la paginación del backend
    const [pagination, setPagination] = useState({
        current: 1, // Página actual
        pageSize: 20, // Elementos por página
        total: 0, // Total de elementos (viene de la API)
    });

    /**
     * Función para obtener postulantes con paginación, búsqueda y filtros.
     */
    const fetchUsers = async (
        page = 1,
        pageSize = 20,
        searchValue = "",
        orderBy = sort.orderBy,
        orderDirection = sort.orderDirection,
        filterParams = filters
    ) => {
        try {
            // Llama a fetchData pasando el tipo "P" (Postulante), página, tamaño, búsqueda y filtros
            const responseData = await fetchData(
                null,
                true,
                "P",
                page,
                pageSize,
                searchValue,
                orderBy,
                orderDirection,
                filterParams
            );

            if (responseData && responseData?.data && responseData?.pagination) {
                const data = responseData?.data;
                const apiPagination = responseData?.pagination;

                setDatasource(data);
                dataExport(data);

                // Actualiza el estado de la paginación con los valores de la API
                setPagination({
                    ...pagination,
                    total: apiPagination?.total,
                    current: apiPagination?.current_page,
                    pageSize: apiPagination?.per_page,
                });
            } else {
                // Manejo de respuesta vacía o error
                setDatasource([]);
                setPagination(prev => ({ ...prev, total: 0 }));
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            setDatasource([]);
            setPagination(prev => ({ ...prev, total: 0 }));
        }
    };

    useEffect(() => {
        // Carga inicial
        fetchUsers(pagination.current, pagination.pageSize, value, sort.orderBy, sort.orderDirection, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchPdf = async (userId) => {
        const data = await getCurriculumPDF(userId);
        if (data?.uuid) {
            window.open(`${BASE_API}cv/${data.filename}`, "_blank", "noreferrer");
        } else {
            openNotification("El curriculum se encuentra incompleto", "success", "Atención!");
        }
    };

    const dataExport = (data) => {
        const exportData = data?.map((item) => {
            return {
                Perfil: item.type === 'I' ? 'Institución' : item.type === 'P' ? 'Postulante' : 'Administrador',
                Nombre: item.name,
                Email: item.email,
                Creado: dayjs(item.created_at).format('DD/MM/YYYY'),
                // El plan suele ser null para postulantes, pero lo mantenemos por seguridad
                Plan: item.plan?.detalle?.nombre,
                Estado: item.cuenta?.rechazado ? "Rechazado" : item.cuenta?.suspendido ? "Suspendido" : item.cuenta?.activo ? "Activo" : "Inactivo",
            }
        })

        setDataExcel(exportData)
    }

    const getEstado = (record) => {
        if (record.cuenta?.rechazado) return "Rechazado";
        if (record.cuenta?.suspendido) return "Suspendido";
        if (record.cuenta?.activo) return "Activo";
        return "Inactivo";
    };

    const columns = [
        {
            title: "Perfil",
            dataIndex: "type",
            key: "type",
            render: (text) => {
                if (text === "I") return <Tag color="lime">Institución</Tag>;
                if (text === "P") return <Tag color="blue">Postulante</Tag>;
                return <Tag color="orange">Administrador</Tag>;
            },
        },
        {
            title: "Nombre",
            dataIndex: "nombre",
            key: "nombre",
            render: (text, record) => (record.type === "I" ? record.cuenta.nombre : record.name),
        },
        {
            title: "Email",
            dataIndex: "email",
        },
        {
            title: "Creado",
            dataIndex: "created_at",
            render: (text) => new Date(text).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }),
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        },
        {
            title: "Última sesión",
            dataIndex: "last_login_at",
            render: (text) => new Date(text).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }),
            sorter: (a, b) => new Date(a.last_login_at) - new Date(b.last_login_at),
        },
        {
            title: "Verificado",
            dataIndex: "verificado",
            render: (text, record) => (
                <Tag color={record.cuenta?.verificado === 0 ? "red" : "green"}>
                    {record.cuenta?.verificado === 0 ? "Pendiente" : "Verificado"}
                </Tag>
            ),
            filters: [
                { text: "Pendiente", value: 0 },
                { text: "Verificado", value: 1 },
            ],
            filteredValue: filters.verificado,
        },
        {
            title: "Estado",
            dataIndex: "estado",
            render: (text, record) => {
                const estado = getEstado(record);
                return <Tag color={estado === "Activo" ? "green" : "red"}>{estado}</Tag>;
            },
            filters: [
                { text: "Rechazado", value: "Rechazado" },
                { text: "Suspendido", value: "Suspendido" },
                { text: "Activo", value: "Activo" },
                { text: "Inactivo", value: "Inactivo" },
            ],
            filterMultiple: true,
            filteredValue: filters.estado,
        },
        {
            title: "IA",
            dataIndex: "disponible_ia",
            render: (text, record) => (
                <Tag color={record.cuenta?.disponible_ia === -1 ? "green" : "red"}>
                    {record.cuenta?.disponible_ia === -1 ? "Si" : "No"}
                </Tag>
            ),
            filters: [
                { text: "Ver", value: -1 },
            ],
            filteredValue: filters.disponible_ia,
        },
        {
            title: "CV",
            dataIndex: "fecha_perfil_completo",
            render: (text, record) => (
                record?.type === "P" &&
                < Tag color={record?.cuenta?.fecha_perfil_completo !== null ? "green" : "red"} >
                    {record?.cuenta?.fecha_perfil_completo !== null ? "Completo" : "Incompleto"}
                </Tag >
            ),
            filters: [
                { text: "Completo", value: "completo" },
                { text: "Incompleto", value: "incompleto" },
            ],
            filteredValue: filters.perfil_completo,
        },
        {
            title: "Acciones",
            dataIndex: "detalle",
            render: (text, record) => (
                <div className="flex items-center gap-1">
                    <Link to={`${routesNames.USER}/${record.id}`} className="bg-main text-sm px-4 py-1 rounded-lg">
                        Ver
                    </Link>
                    {record?.type === "P" && (
                        record?.cuenta?.fecha_perfil_completo !== null &&
                        <a onClick={() => fetchPdf(record?.id)} className="bg-red-500 text-xs text-white px-2 rounded-md py-1 flex items-center">
                            <FaFilePdf className="text-white" /> Descargar
                        </a>
                    )}
                </div>
            ),
        },
    ];


    return (
        <>
            {contextHolder}
            <Input
                allowClear
                placeholder="Buscar por Nombre o Email"
                prefix={<BiSearchAlt className="text-xl" />}
                value={value}
                className="p-3 w-full rounded-lg mb-2"
                onChange={(e) => {
                    const currValue = e.target.value;
                    setValue(currValue);

                    // 🆕 Dispara la búsqueda al backend, volviendo a la página 1
                    fetchUsers(1, pagination.pageSize, currValue, sort.orderBy, sort.orderDirection, filters);

                    // Actualiza la paginación localmente para reflejar el inicio en la página 1
                    setPagination(prev => ({ ...prev, current: 1 }));
                }}
            />

            <div className="flex flex-row justify-between items-center mb-3">
                <span className="block text-sm font-semibold text-start my-1 me-5">
                    {pagination.total} postulantes encontrados
                </span>
                <ExportExcel data={dataExcel} fileName={"Postulantes_" + new Date().toLocaleDateString()} />
            </div>


            <Table
                loading={isLoading}
                dataSource={datasource}
                columns={columns}
                className="w-full"
                rowKey={(record) => record.id}
                size="small"
                // 🆕 Configuración de paginación con solo números
                pagination={{
                    total: pagination.total,
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    showSizeChanger: false, // CLAVE: Deshabilita el selector de cantidad
                    showQuickJumper: false,
                    showTotal: (total, range) => `Mostrando ${range[0]}-${range[1]} de ${total} postulantes`,
                }}
                locale={{ emptyText: "No hay resultados" }}
                onChange={(newPagination, tableFilters, sorter, extra) => {
                    if (extra.action === "sort") {
                        const sorterField = Array.isArray(sorter) ? sorter?.[0]?.field : sorter?.field;
                        const sorterOrder = Array.isArray(sorter) ? sorter?.[0]?.order : sorter?.order;

                        const nextSort = sorterField && sorterOrder
                            ? {
                                orderBy: sorterField,
                                orderDirection: sorterOrder === "ascend" ? "asc" : "desc",
                            }
                            : {
                                orderBy: "last_login_at",
                                orderDirection: "desc",
                            };

                        setSort(nextSort);
                        setPagination((prev) => ({ ...prev, current: 1 }));
                        fetchUsers(1, pagination.pageSize, value, nextSort.orderBy, nextSort.orderDirection, filters);
                    } else if (extra.action === "paginate" && newPagination.current !== pagination.current) {
                        fetchUsers(
                            newPagination.current,
                            pagination.pageSize,
                            value,
                            sort.orderBy,
                            sort.orderDirection,
                            filters
                        );
                    } else if (extra.action === "filter") {
                        const nextFilters = {
                            estado: tableFilters.estado || [],
                            verificado: tableFilters.verificado || [],
                            disponible_ia: tableFilters.disponible_ia || [],
                            perfil_completo: tableFilters.fecha_perfil_completo || [],
                        };

                        setFilters(nextFilters);
                        setPagination((prev) => ({ ...prev, current: 1 }));
                        fetchUsers(1, pagination.pageSize, value, sort.orderBy, sort.orderDirection, nextFilters);
                    }
                    dataExport(extra.currentDataSource)
                }}
            />

        </>
    );
};

export default PostulantesTable;
